import {
  canonicalRepoKeyOf,
  resolveBoardsForGroup,
  resolveProjectGroupRoute,
} from "./projectGroupRoute";
import type { Project } from "./types";

type AddressableProject = Pick<Project, "environmentId" | "id" | "repositoryIdentity">;

interface AddressableBoard {
  readonly id: string;
  readonly githubRepoUrl?: string | null;
}

export interface ProjectRouteInput {
  /** Whatever sits in the URL — a group key, a Forge project id, or a CMMD board id. */
  readonly key: string;
  readonly projects: readonly AddressableProject[];
  readonly boards: readonly AddressableBoard[];
  readonly loading: boolean;
  /**
   * Boards load independently of projects. Kept separate so a slow board fetch
   * cannot make a valid board link look like `notFound`, while still letting a
   * group with a checkout render without waiting for boards at all.
   */
  readonly boardsLoading?: boolean;
}

export type ProjectRouteTarget =
  | { readonly kind: "pending" }
  | { readonly kind: "render"; readonly groupKey: string }
  | {
      readonly kind: "redirect";
      readonly groupKey: string;
      readonly cmmdProjectId?: string;
    }
  | { readonly kind: "notFound" };

/**
 * The whole routing decision for `/project/:key`, as one pure function.
 *
 * A pure function rather than logic inside the route because three of the four
 * outcomes are easy to get wrong in ways a component test would not catch, and
 * one of them — deciding before `projects` have loaded — has already shipped
 * once as a bug in the access gate.
 */
export function resolveProjectRouteTarget(input: ProjectRouteInput): ProjectRouteTarget {
  // A connected checkout gives the best answer, because only it can distinguish
  // a subpath group from the repository root. Tried first, and skipped rather
  // than waited on: a group its boards can vouch for is addressable without it.
  const fromCheckout = input.loading ? null : resolveProjectGroupRoute(input.key, input.projects);
  if (fromCheckout !== null) {
    return fromCheckout.canonical
      ? { kind: "render", groupKey: fromCheckout.groupKey }
      : { kind: "redirect", groupKey: fromCheckout.groupKey };
  }

  const fromBoards =
    input.boardsLoading === true ? null : resolveGroupFromBoards(input.key, input.boards);
  if (fromBoards !== null) return fromBoards;

  // Only now is waiting the right answer. Returning `pending` up front would
  // replace the bounce with a spinner that never resolves in a session where no
  // environment ever connects.
  if (input.loading || input.boardsLoading === true) return { kind: "pending" };

  // Neither a checkout nor a board vouches for this key. That is "cannot be
  // routed", not "does not exist" — but the page can only offer the same answer
  // either way.
  return { kind: "notFound" };
}

/** Where a board page goes once the access gate has evicted it. */
export type BlockedBoardExit =
  | { readonly kind: "group"; readonly groupKey: string }
  | { readonly kind: "root" };

/**
 * The exit for a board the access gate blocked.
 *
 * The gate used to send every evicted board back to its group, on the
 * assumption that the checkout is still there. When no checkout is, the group
 * landing has only the boards to go on, and the evicted board vouches for the
 * group again: the landing redirects straight back to the board, the gate
 * evicts again, and the two routes bounce forever, adding a notice on every
 * pass (forge#3491). So the exit is the group only when a connected checkout
 * renders it, and the app root otherwise.
 */
export function resolveBlockedBoardExit(input: ProjectRouteInput): BlockedBoardExit {
  // Only a checkout may answer here. A board-vouched group also "renders", but
  // its landing would send the evicted board straight back.
  const fromCheckout = input.loading ? null : resolveProjectGroupRoute(input.key, input.projects);
  if (fromCheckout === null) return { kind: "root" };
  // The landing forwards a single-board group to that board, so the group is
  // a safe exit only when it has other boards to choose from.
  const landing = resolveGroupLanding(fromCheckout.groupKey, input.boards);
  return landing.kind === "choose"
    ? { kind: "group", groupKey: fromCheckout.groupKey }
    : { kind: "root" };
}

/**
 * The group a key addresses using boards alone, or `null`.
 *
 * Separate because it is the whole point of the surface: a board names its own
 * repository, and a group key names a repository rather than a checkout. Making
 * this depend on a connected environment is what made every cloud project 404
 * until its runtime happened to be awake.
 */
function resolveGroupFromBoards(
  key: string,
  boards: readonly AddressableBoard[],
): ProjectRouteTarget | null {
  // Every link written before group keys existed carries a CMMD board id, and
  // it is still what the sidebar navigates with.
  const board = boards.find((candidate) => candidate.id === key);
  const boardRepoKey = board ? canonicalRepoKeyOf(board.githubRepoUrl) : null;
  if (board && boardRepoKey !== null) {
    return { kind: "redirect", groupKey: boardRepoKey, cmmdProjectId: board.id };
  }

  // Canonicalise-on-arrival. The canonical key is lower-cased while the URLs
  // people copy are not, so comparing byte-exact would reject a repo key on its
  // own repository.
  const repoKey = canonicalRepoKeyOf(key);
  if (repoKey === null || resolveBoardsForGroup(repoKey, boards).length === 0) return null;
  return repoKey === key
    ? { kind: "render", groupKey: repoKey }
    : { kind: "redirect", groupKey: repoKey };
}

export type GroupLanding =
  | { readonly kind: "board"; readonly cmmdProjectId: string }
  | { readonly kind: "choose"; readonly cmmdProjectIds: readonly string[] }
  | { readonly kind: "empty" };

/**
 * Where `/project/:groupKey` should land, given the boards on that repo.
 *
 * Several boards is the DESIGNED case, not an edge one — CMMD's schema is
 * explicitly many-to-many. So the answer for "many" is `choose`, never a pick:
 * choosing for the user would be a silent decision between equals, and the one
 * chosen would change as boards are added or removed.
 */
export function resolveGroupLanding(
  groupKey: string,
  boards: readonly AddressableBoard[],
): GroupLanding {
  const linked = resolveBoardsForGroup(groupKey, boards);
  const [first, second] = linked;
  if (first === undefined) return { kind: "empty" };
  if (second === undefined) return { kind: "board", cmmdProjectId: first.id };
  return { kind: "choose", cmmdProjectIds: linked.map((board) => board.id) };
}

export type LandingNavigation =
  | { readonly kind: "stay" }
  | { readonly kind: "leave" }
  | {
      readonly kind: "navigate";
      /** The route to go to, already chosen — the caller does not re-derive it. */
      readonly to: "/project/$projectKey" | "/project/$projectKey/tasks/$cmmdProjectId";
      readonly params: { readonly projectKey: string; readonly cmmdProjectId?: string };
    };

/**
 * What the landing surface should DO, given the route target and the group's
 * boards.
 *
 * Pure, and separate from the component, because branching inside a component
 * is the shape every routing bug on this surface has taken. `stay` is a real
 * outcome rather than a default: a group that renders a picker or an empty
 * state must NOT navigate, or the picker becomes unreachable.
 */
export function resolveLandingNavigation(
  target: ProjectRouteTarget,
  landing: GroupLanding,
  tab?: string,
): LandingNavigation {
  if (target.kind === "pending") return { kind: "stay" };
  if (target.kind === "notFound") return { kind: "leave" };
  // A redirect corrects the URL itself, so it outranks any tab: a mis-cased key
  // must not stick on a page whose own address is wrong.
  if (target.kind === "redirect") return navigateTo(target.groupKey, target.cmmdProjectId);
  // Settings belong to the group — the name, the checkouts, the delete — none
  // of which are properties of any one board. Falling through to the board
  // would make the group's settings unreachable whenever the repo has exactly
  // one board, which is the common case.
  if (tab !== undefined && isGroupScopedTab(tab)) return { kind: "stay" };
  return landing.kind === "board"
    ? navigateTo(target.groupKey, landing.cmmdProjectId)
    : { kind: "stay" };
}

/** Tabs that describe the checkout group rather than a CMMD board. */
function isGroupScopedTab(tab: string): boolean {
  return tab === "settings";
}

/** One place that decides which of the two routes a navigation means. */
function navigateTo(projectKey: string, cmmdProjectId: string | undefined): LandingNavigation {
  return cmmdProjectId === undefined
    ? { kind: "navigate", to: "/project/$projectKey", params: { projectKey } }
    : {
        kind: "navigate",
        to: "/project/$projectKey/tasks/$cmmdProjectId",
        params: { projectKey, cmmdProjectId },
      };
}
